import { useState, useEffect, useContext } from 'react';
import { Box, TextField } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { usePOSConfig, useTranslations } from '../../../lib/hooks';
import { AppContext, SttButton, SttDialog } from '../../../sporttia/all';
import { requestAdHoc } from '../../../lib/utils';

type Props = {
    open: boolean;
    onClose: () => void;
    onAdd?: (ip: string) => void;
};

type POSConfig = {
    init: {
        method: string;
        uri: string;
        params: () => unknown;
    };
};

type POSInitResponse = {
    resultCode: string;
    [key: string]: unknown;
};

export default function ConfigurationPosDialog({
    open,
    onClose,
    onAdd,
}: Props): React.ReactElement {
    const { translate } = useTranslations();
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const [ip, setIp] = useState<string>('');
    const [forceInputFocus, setForceInputFocus] = useState<boolean>(false);
    const [testing, setTesting] = useState<boolean>(false);
    const [tested, setTested] = useState<boolean>(false);

    // Config local del datáfono
    const [POSConfig, setPOSConfig] = useState<POSConfig | null>(null);

    // Esto es una función que hay que llamar con la IP del dispositivo
    const setupPOSConfig = usePOSConfig('PAYCOMET');

    useEffect(() => {
        setForceInputFocus(false);
    }, [open]);

    useEffect(() => {
        if (ip !== '') {
            setForceInputFocus(true);
        }
        setTested(false);
        setPOSConfig(setupPOSConfig(ip));
    }, [ip]);

    useEffect(() => {
        setIp('');
    }, [open]);

    function addDevice(): void {
        if (!tested) {
            testDevice()
                .then((result: any) => {
                    if (result) {
                        onAdd?.(ip);
                    } else {
                        cxt?.showMessage(
                            'E',
                            t('config.pos.deviceTestNotFound'),
                        );
                    }
                })
                .catch((error: string) => {
                    cxt?.showMessage('E', error);
                });
        } else {
            onAdd?.(ip);
        }
    }

    function testDevice(): Promise<boolean> {
        return new Promise((resolve: any, reject: any) => {
            if (ip) {
                if (POSConfig) {
                    setTesting(true);

                    requestAdHoc(
                        POSConfig.init.method,
                        POSConfig.init.uri,
                        POSConfig.init.params() as Record<string, unknown>,
                    )
                        .then((response: any) => {
                            const posResponse = response as POSInitResponse;
                            if (parseInt(posResponse.resultCode) === 1000) {
                                resolve(true);
                            } else {
                                resolve(false);
                            }
                        })
                        .catch(() => {
                            reject(t('config.pos.deviceTestError'));
                        })
                        .finally(() => {
                            setTesting(false);
                        });
                } else {
                    reject(t('config.pos.deviceTestInvalidIP'));
                }
            } else {
                reject(t('config.pos.deviceTestEmptyIP'));
            }
        });
    }

    return (
        <SttDialog
            title={t('config.pos.addDeviceTitle')}
            open={open}
            onClose={onClose}
            content={
                <div>
                    <Box display="flex" alignItems="flex-start">
                        <TextField
                            fullWidth
                            type="text"
                            variant="outlined"
                            label={t('config.pos.IPPlaceholderLabel')}
                            focused={forceInputFocus}
                            size="small"
                            value={ip}
                            onChange={(e: any) => {
                                setIp(e.target.value);
                            }}
                            helperText={t('config.pos.IPHelpText')}
                        />
                        <Box ml={2}>
                            <SttButton
                                startIcon={<Add />}
                                caption={translate('Add')}
                                color="primary"
                                variant="contained"
                                disabled={testing}
                                onClick={addDevice}
                            />
                        </Box>
                    </Box>

                    <Box display="flex" mt={3}>
                        <SttButton
                            startIcon={<Add />}
                            loading={testing}
                            caption={t('config.pos.deviceTestLabel')}
                            color="inherit"
                            variant="contained"
                            disabled={testing}
                            onClick={() => {
                                testDevice()
                                    .then((result: any) => {
                                        if (result) {
                                            cxt?.showMessage(
                                                'S',
                                                t('config.pos.deviceTestOK'),
                                            );
                                        } else {
                                            cxt?.showMessage(
                                                'E',
                                                t(
                                                    'config.pos.deviceTestNotFound',
                                                ),
                                            );
                                        }
                                    })
                                    .catch((msg: string) => {
                                        cxt?.showMessage('E', msg);
                                    });
                            }}
                        />
                    </Box>
                </div>
            }
            buttons={[]}
        />
    );
}
